import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Chip,
  Slide,
  Stack
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

// Example frame data (15 frames)
const initialFrameData = Array.from({ length: 15 }, (_, i) => ({
  title: `Frame Model ${i + 1}`,
  style: ["Classic", "Modern", "Vintage", "Minimal", "Luxury"][i % 5],
  img: `https://source.unsplash.com/featured/?frame,art,${i + 1}`,
  desc: `Attractive ${["classic", "modern", "vintage", "minimal", "luxury"][i % 5]} style frame for your best moments.`,
  details: `Frame Model ${i + 1}\n\nStyle: ${["Classic", "Modern", "Vintage", "Minimal", "Luxury"][i % 5]}\nMaterial: Premium Wood\nSize: 12x18, 16x24, 20x30\nFinish: Glossy/Matte\nCustomization: Available\nPrice: ₹${2000 + i * 500}`
}));

const transition = (props) => <Slide direction="up" {...props} />;

function Frame() {
  const clientMongoId = localStorage.getItem("clientMongoId");
  const [frameData, setFrameData] = useState([...initialFrameData]);
  const [open, setOpen] = useState(false);
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [editIdx, setEditIdx] = useState(null);
  const [editImg, setEditImg] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editDetails, setEditDetails] = useState("");

  // Fetch frames from DB
  useEffect(() => {
    async function fetchFrames() {
      if (!clientMongoId) return;
      try {
        const res = await axios.get(`https://studio-lc.onrender.com/api/clients/${clientMongoId}/frames`);
        setFrameData(res.data.frames && res.data.frames.length > 0 ? res.data.frames : initialFrameData);
      } catch (err) {
        setFrameData(initialFrameData);
      }
    }
    fetchFrames();
  }, [clientMongoId]);

  // Save frames to DB
  const saveFramesToDB = async (frames) => {
    try {
      await axios.put(`https://studio-lc.onrender.com/api/clients/${clientMongoId}/frames`, {
        frames
      });
      alert("Frames updated!");
    } catch (err) {
      alert("Failed to update frames.");
    }
  };

  // Card click handler
  const handleCardClick = (frame) => {
    setSelectedFrame(frame);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedFrame(null);
  };

  // Edit dialog handlers
  const handleEdit = (idx) => {
    setEditIdx(idx);
    setEditImg(frameData[idx].img);
    setEditTitle(frameData[idx].title);
    setEditDesc(frameData[idx].desc);
    setEditDetails(frameData[idx].details);
  };

  const handleSaveEdit = async () => {
    const updated = [...frameData];
    updated[editIdx] = {
      ...updated[editIdx],
      img: editImg,
      title: editTitle,
      desc: editDesc,
      details: editDetails
    };
    setFrameData(updated);
    setEditIdx(null);
    await saveFramesToDB(updated);
  };

  // Delete handler
  const [deleteIdx, setDeleteIdx] = useState(null);
  const handleDelete = async () => {
    const updated = [...frameData];
    updated.splice(deleteIdx, 1);
    setFrameData(updated);
    setDeleteIdx(null);
    await saveFramesToDB(updated);
  };

  // Add new frame handler
  const [addOpen, setAddOpen] = useState(false);
  const [newImg, setNewImg] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newDetails, setNewDetails] = useState("");
  const handleAddFrame = async () => {
    const updated = [
      ...frameData,
      {
        title: newTitle || "New Frame",
        style: "Custom",
        img: newImg || "https://via.placeholder.com/300x200?text=Frame",
        desc: newDesc,
        details: newDetails
      }
    ];
    setFrameData(updated);
    setAddOpen(false);
    setNewImg("");
    setNewTitle("");
    setNewDesc("");
    setNewDetails("");
    await saveFramesToDB(updated);
  };

  return (
    <Box sx={{ pt: 2, px: 2, bgcolor: "#f9f9f9", minHeight: "100vh" }}>
    <Typography
     gutterBottom
     variant="h5"
     component="div"
     sx={{
       fontWeight: 700,
       color: "#fff",
       textAlign: "center",
       background: "linear-gradient(90deg, #1976d2 60%, #64b5f6 100%)",
       borderRadius: 3,
       px: 2,
       py: 1,
       boxShadow: "0 2px 8px #1976d244",
       mb: 2,
       letterSpacing: 1.5,
        fontFamily: "Josefin Sans",height: 60, fontSize:40
     }}
   >
        Frames
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 5 }}>
        <Button sx={{fontFamily:"Jost"}}variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
          Add New Frame
        </Button>
      </Box>
      <Grid container spacing={6} justifyContent="center">
        {frameData.map((frame, idx) => (
          <Grid item xs={12} sm={6} md={4} key={frame.title}>
            <Card
              sx={{
                width: 380,           // Fixed width
                minWidth: 300,
                maxWidth: 360,
                borderRadius: 4,
                boxShadow: 4,
                transition: "transform 0.3s",
                "&:hover": {
                  transform: "scale(1.04)",
                  boxShadow: 8,
                  background: "linear-gradient(90deg,#e3f2fd 60%,#fff 100%)"
                },
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start"
              }}
              onClick={() => handleCardClick(frame)}
            >
              <CardActionArea>
                <CardMedia
                  component="img"
                  height="160"
                  image={frame.img}
                  alt={frame.title}
                  sx={{
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    animation: "frameFade 2s"
                  }}
                />
                <CardContent
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        minHeight: 0,
        overflow: "hidden",
        pb: "0 !important"
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
        <Chip sx={{ fontFamily:"Josefin Sans",background:"#1976d2",color:"#fff"}} label={frame.style} color="primary" variant="outlined" />
      </Stack>
      <Typography
        gutterBottom
        variant="h5"
        component="div"
        sx={{
          fontWeight: 700,
          color: "#1976d2",
          textAlign: "center",
          fontFamily:"Josefin Sans",
          fontSize: 20,
          mb: 1
        }}
      >
        {frame.title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          textAlign: "center",
          fontWeight: 500,
          fontFamily:"Jost",
          mb: 1,
          wordBreak: "break-word",
          overflowWrap: "break-word",
          maxWidth: "100%",
          fontSize: 14,
          lineHeight: 1.5,
          maxHeight: 42, // About 2 lines
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical"
        }}
      >
        {frame.desc}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1 }}>
        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation();
            handleEdit(idx);
          }}
          sx={{ bgcolor: "#fff", boxShadow: 1, "&:hover": { bgcolor: "#e3f2fd" } }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation();
            setDeleteIdx(idx);
          }}
          sx={{ bgcolor: "#fff", boxShadow: 1, "&:hover": { bgcolor: "#fdecea" } }}
        >
          <DeleteIcon fontSize="small" sx={{ color: "#d32f2f", }} />
        </IconButton>
      </Box>
    </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
      {/* Frame Details Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={transition}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: 8,
            bgcolor: "#fff",
            animation: "frameDialogFade 1s"
          }
        }}
      >
        {selectedFrame && (
          <>
            <DialogTitle
              sx={{
                fontWeight: 700,
                color: "#1976d2",
                textAlign: "center",
                fontSize: 28,
              fontFamily:"Josefin Sans",
                letterSpacing: 2
              }}
            >
              {selectedFrame.title}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <img
                  src={selectedFrame.img}
                  alt={selectedFrame.title}
                  style={{
                    width: "100%",
                    maxWidth: 320,
                    height: 180,
                    objectFit: "cover",
                    borderRadius: 12,
                    marginBottom: 16,
                    boxShadow: "0 4px 16px rgba(25,118,210,0.10)",
                    animation: "frameFade 2s"
                  }}
                />
                <Chip  sx={{background:"#1976d2",color:"#fff",fontFamily:"Josefin Sans",mb:2}}label={selectedFrame.style} color="primary" variant="outlined"  />
                <Typography
                  variant="body1"
                  sx={{
                    color: "#333",
                    fontWeight: 500,
                    textAlign: "center",
                    whiteSpace: "pre-line",
                    fontFamily:"Jost",
                    mb: 2,
                    fontSize: 18,
                    animation: "frameTextAnim 1.2s"
                  }}
                >
                  {selectedFrame.details}
                </Typography>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
      {/* Edit Dialog */}
      <Dialog open={editIdx !== null} onClose={() => setEditIdx(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Frame</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
            <img
              src={editImg}
              alt="Frame"
              style={{
                width: 180,
                height: 120,
                objectFit: "cover",
                borderRadius: 8,
                marginBottom: 8,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(25,118,210,0.10)"
              }}
              onClick={() => document.getElementById("edit-frame-image-input").click()}
            />
            <input
              id="edit-frame-image-input"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={async (e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (ev) => setEditImg(ev.target.result);
                  reader.readAsDataURL(file);
                }
              }}
            />
            <Typography variant="caption" sx={{ color: "#1976d2", cursor: "pointer" }} onClick={() => document.getElementById("edit-frame-image-input").click()}>
              Click image to change
            </Typography>
          </Box>
          <TextField
            label="Title"
            fullWidth
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            sx={{ mb: 2, width: 400 }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={editDesc}
            onChange={e => setEditDesc(e.target.value)}
            sx={{ mb: 2, width: 400 }}
          />
          <TextField
            label="Details"
            fullWidth
            multiline
            minRows={4}
            value={editDetails}
            onChange={e => setEditDetails(e.target.value)}
            sx={{ width: 400 }}
          />
        </DialogContent>
        <DialogActions>
          <Button sx={{fontFamily:"Josefin Sans",}} onClick={() => setEditIdx(null)}>Cancel</Button>
          <Button sx={{fontFamily:"Josefin Sans",}} variant="contained" onClick={handleSaveEdit}>Save</Button>
        </DialogActions>
      </Dialog>
      {/* Add Frame Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Frame</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
            <img
              src={newImg || "https://via.placeholder.com/180x120?text=Frame"}
              alt="New Frame"
              style={{
                width: 180,
                height: 120,
                objectFit: "cover",
                borderRadius: 8,
                marginBottom: 8,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(25,118,210,0.10)"
              }}
              onClick={() => document.getElementById("add-frame-image-input").click()}
            />
            <input
              id="add-frame-image-input"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={async (e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onload = (ev) => setNewImg(ev.target.result);
                  reader.readAsDataURL(file);
                }
              }}
            />
            <Typography variant="caption" sx={{ color: "#1976d2", cursor: "pointer" }} onClick={() => document.getElementById("add-frame-image-input").click()}>
              Click image to change
            </Typography>
          </Box>
          <TextField
            label="Title"
            fullWidth
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            sx={{ mb: 2, width: 400 }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={newDesc}
            onChange={e => setNewDesc(e.target.value)}
            sx={{ mb: 2, width: 400 }}
          />
          <TextField
            label="Details"
            fullWidth
            multiline
            minRows={4}
            value={newDetails}
            onChange={e => setNewDetails(e.target.value)}
            sx={{ width: 400 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddFrame}>Add</Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteIdx !== null} onClose={() => setDeleteIdx(null)}>
        <DialogTitle sx={{fontFamily:"Josefin Sans",}}>Are you sure you want to delete this frame?</DialogTitle>
        <DialogActions>
          <Button sx={{fontFamily:"Josefin Sans",}}onClick={() => setDeleteIdx(null)}>No</Button>
          <Button sx={{fontFamily:"Josefin Sans",}} variant="contained" color="error" onClick={handleDelete}>
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* Animation styles */}
      <style>
        {`
          @keyframes frameFade {
            0% { opacity: 0; transform: scale(0.96);}
            100% { opacity: 1; transform: scale(1);}
          }
          @keyframes frameDialogFade {
            0% { opacity: 0; transform: translateY(40px);}
            100% { opacity: 1; transform: translateY(0);}
          }
          @keyframes frameTextAnim {
            0% { opacity: 0; transform: translateY(20px);}
            100% { opacity: 1; transform: translateY(0);}
          }
        `}
      </style>
    </Box>
  );
}

export default Frame;